import { writable } from "svelte/store";

// Keep the wire format and the parameter names in sync with public/sw.js — a
// service worker cannot import these across all browsers.
const SW_PATH = "/sw.js";
const CONFIG_PARAM = "swCfg";
const CONFIG_VERSION = 1;
const KILL_PARAM = "dev-proxy";
const PANEL_PARAM = "dev-proxy";
const PANEL_VALUE = "1";
const KILL_VALUE = "off";

/**
 * The friendly catalogue behind the panel: a target is picked by name, the path
 * prefix it rewrites is an implementation detail nobody should have to remember.
 * `entry` is the file used to verify a target is actually reachable.
 */
export const DEV_PROXY_TARGETS = [
  {
    id: "event-manager",
    label: "Event Manager",
    hint: "Channel Manager UI, mounted under /channel-manager",
    prefix: "/event-manager-frontend/",
    entry: "event-manager.es.js",
    example: "https://branch.dev.gl.glcloud.eu/event-manager-frontend-mr-15/",
  },
  {
    id: "cut-manager",
    label: "Cut Manager",
    hint: "Mounted under /cut-manager",
    prefix: "/cut-manager-frontend/",
    entry: "cut-manager.es.js",
    example: "https://branch.dev.gl.glcloud.eu/cut-manager-frontend-mr-7/",
  },
  {
    id: "content-manager",
    label: "Content Manager",
    hint: "Mounted under /content-manager, includes the EUP player assets",
    prefix: "/content-manager-frontend/",
    entry: "content-manager.js",
    example: "https://branch.dev.gl.glcloud.eu/content-manager-frontend-mr-3/",
  },
  {
    id: "rundown",
    label: "Rundown",
    hint: "",
    prefix: "/rundown-fe/",
    entry: "rundown.es.js",
    example: "https://branch.dev.gl.glcloud.eu/rundown-frontend-mr-3/",
  },
  {
    id: "distributor",
    label: "Distributor",
    hint: "",
    prefix: "/compositor-distributor-fe/",
    entry: "compositor-distributor.es.js",
    example: "https://branch.dev.gl.glcloud.eu/rundown-frontend-mr-3/",
  },
  {
    id: "shell",
    label: "Access Manager (this app)",
    hint: "Serves another build of the shell on this origin — keeps your session, tokens and API of the current environment",
    kind: "shell",
    entry: "index.html",
    example: "https://branch.dev.gl.glcloud.eu/access-manager-frontend-mr-6/",
  },
];

export const activeConfig = writable(null);

export function isSupported() {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

export function killSwitchUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set(KILL_PARAM, KILL_VALUE);

  return url.href;
}

export function isPanelRequestedByUrl() {
  return new URLSearchParams(window.location.search).get(PANEL_PARAM) === PANEL_VALUE;
}

export function validatePrefix(prefix) {
  if (!prefix) return "Required";
  if (!prefix.startsWith("/")) return "Must start with /";
  if (!prefix.endsWith("/")) return "Must end with /";

  return null;
}

function withTrailingSlash(url) {
  return url.endsWith("/") ? url : `${url}/`;
}

/**
 * Verifies a target before it is committed, so a typo surfaces here instead of as
 * a blank page after the reload.
 */
export async function probeTarget(target, entry) {
  const url = `${withTrailingSlash(target)}${entry}`;
  let reachable = false;

  for (const method of ["HEAD", "GET"]) {
    try {
      const response = await fetch(url, {
        method,
        mode: "cors",
        credentials: "omit",
        cache: "no-store",
      });

      reachable = true;

      if (response.ok) return { ok: true };
    } catch {
      // Either the host is down or CORS is missing — both are reported below.
    }
  }

  return {
    ok: false,
    message: reachable
      ? `${entry} not found at ${target}`
      : `${target} is unreachable or does not allow this origin via CORS`,
  };
}

export function buildConfig(draft) {
  const rules = [];

  for (const target of DEV_PROXY_TARGETS) {
    if (target.kind === "shell") continue;

    const value = draft.targets[target.id]?.trim();

    if (value) {
      rules.push({
        id: target.id,
        prefix: target.prefix,
        target: withTrailingSlash(value),
      });
    }
  }

  for (const rule of draft.custom) {
    if (rule.prefix?.trim() && rule.target?.trim()) {
      rules.push({
        id: "custom",
        prefix: rule.prefix.trim(),
        target: withTrailingSlash(rule.target.trim()),
      });
    }
  }

  const shell = draft.targets.shell?.trim();

  return {
    v: CONFIG_VERSION,
    shell: shell ? withTrailingSlash(shell) : null,
    rules,
  };
};

export function isEmptyConfig(built) {
  return !built.shell && !built.rules.length;
}

/** The registration URL is the whole contract with public/sw.js — see parseConfig() there. */
export function scriptUrl(built) {
  return `${SW_PATH}?${CONFIG_PARAM}=${encodeURIComponent(JSON.stringify(built))}`;
}

/** Mirrors parseConfig() in public/sw.js — same wire format, same readable shape. */
function parseScriptUrl(url) {
  try {
    const raw = new URL(url, window.location.origin).searchParams.get(
      CONFIG_PARAM,
    );

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (parsed?.v !== CONFIG_VERSION) return null;

    return {
      shell: parsed.shell ?? null,
      rules: Array.isArray(parsed.rules) ? parsed.rules : [],
    };
  } catch {
    return null;
  }
}

async function registrations() {
  return isSupported() ? navigator.serviceWorker.getRegistrations() : [];
}

/**
 * The registration is the single source of truth, which is what lets an
 * overridden shell build read back the same state that switched it on.
 */
export async function readActiveConfig() {
  const [registration] = await registrations();
  const worker =
    registration?.active ?? registration?.waiting ?? registration?.installing;
  const current = worker ? parseScriptUrl(worker.scriptURL) : null;

  activeConfig.set(current);

  return current;
}

/**
 * Rejects on "redundant" instead of resolving: a worker that dies during install
 * would otherwise trigger a reload that looks successful but changes nothing.
 */
function waitForActivation(registration) {
  return new Promise((resolve, reject) => {
    const worker = registration.installing ?? registration.waiting;

    if (!worker || registration.active === worker) {
      resolve();
      return;
    }

    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") resolve();
      if (worker.state === "redundant") {
        reject(new Error("the worker was discarded before it activated"));
      }
    });
  });
}

export async function apply(built) {
  if (isEmptyConfig(built)) {
    await disable();
    return;
  }

  const registration = await navigator.serviceWorker.register(
    scriptUrl(built),
    { scope: "/" },
  );

  await waitForActivation(registration);
  window.location.reload();
}

export async function disable() {
  const existing = await registrations();

  await Promise.all(existing.map((registration) => registration.unregister()));
  activeConfig.set(null);
  window.location.reload();
}
